import React from 'react';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-lg font-bold text-slate-900 mb-3">{title}</h2>
      <div className="text-slate-600 text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function Bold({ children }: { children: React.ReactNode }) {
  return <span className="font-semibold text-slate-800">{children}</span>;
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50">

      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-blue-600 rounded-md flex items-center justify-center shadow-sm">
              <span className="text-white font-black text-xs italic">V</span>
            </div>
            <span className="font-bold text-slate-900 text-[15px]">Visa Hub</span>
          </a>
          <a href="/" className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
            ← Back to app
          </a>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10">
          <h1 className="text-3xl font-black text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-slate-400">Last updated: April 6, 2026</p>
        </header>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-8 py-10">

          <p className="text-slate-600 text-sm leading-relaxed mb-8">
            VisaHub ("VisaHub," "we," "us," or "our") operates the VisaHub mobile application, the website
            located at visahub.info, and related services (collectively, the "Service"). This Privacy Policy
            explains how we collect, use, disclose, and protect information when you use the Service. By using
            the Service, you agree to the practices described in this Privacy Policy.
          </p>

          <Section title="1. Information we collect">
            <p>We may collect the following categories of information:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><Bold>Account information:</Bold> name, email address, password, and other details you provide when you create an account.</li>
              <li><Bold>Profile and travel information:</Bold> passport nationality, destination country, residence country, travel dates, trip purpose, visa preferences, and similar travel-related details you choose to save.</li>
              <li><Bold>Visa and document information:</Bold> passport details, citizenship or nationality, document uploads, notes, and saved searches related to visa requirements or entry policies.</li>
              <li><Bold>Communications:</Bold> messages, support requests, feedback, and any information you send to us.</li>
              <li><Bold>Usage information:</Bold> pages viewed, features used, actions taken in the app, timestamps, referral data, and interaction patterns.</li>
              <li><Bold>Device and technical information:</Bold> IP address, browser type, device model, operating system, app version, identifiers, crash logs, and diagnostic data.</li>
              <li><Bold>Cookies and similar technologies:</Bold> we may use cookies, local storage, pixels, and analytics tools to operate and improve the Service.</li>
            </ul>
            <p>We generally collect information directly from you, automatically from your device, and from third-party service providers that help us run the Service.</p>
          </Section>

          <Section title="2. How we use information">
            <p>We may use the information we collect to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Provide, operate, and maintain the Service.</li>
              <li>Generate visa requirement results, destination entry guidance, reminders, summaries, and alerts.</li>
              <li>Save your preferences, search history, and account settings.</li>
              <li>Improve accuracy, performance, personalization, and user experience.</li>
              <li>Communicate with you about the Service, updates, security notices, and support responses.</li>
              <li>Analyze usage trends and diagnose technical issues.</li>
              <li>Detect, prevent, and investigate fraud, abuse, security incidents, or unauthorized access.</li>
              <li>Comply with legal obligations and enforce our Terms of Service.</li>
            </ul>
          </Section>

          <Section title="3. How we share information">
            <p>We do not sell your personal information.</p>
            <p>We may share information in the following circumstances:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><Bold>Service providers:</Bold> with vendors that help us host the app, store data, analyze usage, send notifications, provide customer support, or secure the Service.</li>
              <li><Bold>Third-party integrations:</Bold> with providers you choose to connect to the Service, or that are necessary to enable certain features.</li>
              <li><Bold>Legal and safety reasons:</Bold> when required by law, subpoena, court order, government request, or when we believe disclosure is necessary to protect rights, safety, or security.</li>
              <li><Bold>Business transfers:</Bold> in connection with a merger, acquisition, financing, reorganization, or sale of assets.</li>
              <li><Bold>At your direction:</Bold> when you choose to share content, saved trips, or other information with other users or collaborators.</li>
            </ul>
            <p>If you use any sharing or collaboration features, information you make available to others may be visible to the people you choose.</p>
          </Section>

          <Section title="4. Third-party services">
            <p>
              The Service may rely on third-party services such as cloud hosting, analytics, crash reporting,
              authentication, notifications, maps, and payment processing. These third parties may collect or
              process information according to their own privacy policies and terms. We encourage you to review
              those policies before using the relevant services.
            </p>
          </Section>

          <Section title="5. Visa and travel information">
            <p>
              VisaHub provides informational travel and visa guidance only. Your search history, saved trips,
              passport profile, and destination preferences may be used to generate results and recommendations
              tailored to your inputs.
            </p>
            <p>
              We may use aggregated or de-identified information to improve visa content, search relevance, and
              product performance. We may also use your data to deliver destination-specific alerts or updates
              if you enable those features.
            </p>
          </Section>

          <Section title="6. Data retention">
            <p>
              We retain personal information only for as long as reasonably necessary to provide the Service,
              comply with legal obligations, resolve disputes, enforce agreements, and support legitimate
              business purposes. Retention periods may vary depending on the type of information, how it is
              used, and whether deletion is requested. Some information may be retained longer if required by
              law, security needs, or recordkeeping obligations.
            </p>
          </Section>

          <Section title="7. Security">
            <p>
              We use reasonable administrative, technical, and organizational safeguards designed to protect
              your information. However, no method of transmission or storage is completely secure, and we
              cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="8. Your choices">
            <p>Depending on your location, you may have choices regarding your personal information, including the ability to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Access the information we hold about you.</li>
              <li>Correct inaccurate or incomplete information.</li>
              <li>Delete certain information or your account.</li>
              <li>Export certain information, where supported.</li>
              <li>Object to or restrict certain processing.</li>
              <li>Withdraw consent where processing is based on consent.</li>
            </ul>
            <p>You can also manage some preferences directly in the app, such as notifications, saved searches, and profile settings.</p>
          </Section>

          <Section title="9. Children's privacy">
            <p>
              The Service is not directed to children under 18, and we do not knowingly collect personal
              information from them without appropriate parental or legal guardian consent where required by
              law. If we learn that we collected personal information from a child without valid consent, we
              will take steps to delete it.
            </p>
          </Section>

          <Section title="10. International users">
            <p>
              The Service may be operated from the United States or other countries. If you access the Service
              from outside the country where our servers or service providers are located, your information may
              be transferred to, stored in, and processed in other jurisdictions. By using the Service, you
              understand that laws in those jurisdictions may differ from those in your home country.
            </p>
          </Section>

          <Section title="11. California privacy rights">
            <p>
              If you are a California resident, you may have rights under California law regarding your
              personal information, including the right to know, delete, and correct certain information, and
              to limit certain uses of sensitive personal information where applicable.
            </p>
            <p>
              We do not sell personal information as that term is commonly defined under California law, and we
              do not knowingly share personal information for cross-context behavioral advertising unless
              clearly disclosed and enabled by us.
            </p>
          </Section>

          <Section title="12. Changes to this policy">
            <p>
              We may update this Privacy Policy from time to time. If we make material changes, we will post
              the updated version in the Service and revise the "Last updated" date above. Your continued use
              of the Service after the updated policy becomes effective means you accept the revised policy.
            </p>
          </Section>

          <Section title="13. Contact us">
            <p>If you have questions about this Privacy Policy or your personal information, contact us at:</p>
            <div className="mt-3 p-4 bg-slate-50 rounded-xl border border-slate-100 text-sm space-y-1">
              <p className="font-semibold text-slate-800">Gaillimo Labs</p>
              <p>Email: <a href="mailto:support@gaillimolabs.com" className="text-blue-600 hover:underline">support@gaillimolabs.com</a></p>
              <p>Website: <a href="https://visahub.info" className="text-blue-600 hover:underline">visahub.info</a></p>
            </div>
          </Section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6 mt-4">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span>© 2026 Gaillimo Labs. All rights reserved.</span>
          <a href="/" className="hover:text-slate-600 transition-colors">← Back to Visa Hub</a>
        </div>
      </footer>

    </div>
  );
}
